import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSiteImages } from "@/context/SiteImagesContext";
import { useSiteContent } from "@/context/SiteContentContext";

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

const ANATOMY = [
  { titleKey: "anat1_title", descKey: "anat1_desc", imgKey: "irisGrey" },
  { titleKey: "anat2_title", descKey: "anat2_desc", imgKey: "irisBlue" },
  { titleKey: "anat3_title", descKey: "anat3_desc", imgKey: "irisAmber" },
  { titleKey: "anat4_title", descKey: "anat4_desc", imgKey: "irisTeal" },
];

export const UniquenessReport = () => {
  const { img } = useSiteImages();
  const { t } = useSiteContent();
  const score = 69;
  const scale = (t('score_scale') || "").split("\n").filter(Boolean);
  return (
    <section id="uniqueness" className="relative py-28 px-6" data-testid="uniqueness-section">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }} transition={{ duration: 0.6 }}
          className="text-center max-w-2xl mx-auto"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
            {t('uniqueness_title')} <span className="text-gradient">{t('uniqueness_highlight')}</span>
          </h2>
          <p className="mt-4 text-white/55 text-lg">{t('uniqueness_subtitle')}</p>
        </motion.div>

        <div className="mt-14 grid lg:grid-cols-5 gap-6 items-stretch">
          <motion.div
            initial={{ opacity: 0, x: -24 }} whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.6 }}
            className="lg:col-span-2 glass rounded-2xl p-8 flex flex-col justify-center"
            data-testid="score-widget"
          >
            <h3 className="text-xl font-semibold">{t('score_title')}</h3>
            <p className="text-white/55 text-sm mt-1">{t('score_desc')}</p>

            <div className="mt-8 flex items-center gap-6">
              <div className="relative h-32 w-32 shrink-0">
                <svg viewBox="0 0 120 120" className="h-full w-full -rotate-90">
                  <circle cx="60" cy="60" r="52" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="12" />
                  <circle
                    cx="60" cy="60" r="52" fill="none" stroke="url(#g)" strokeWidth="12" strokeLinecap="round"
                    strokeDasharray={2 * Math.PI * 52}
                    strokeDashoffset={2 * Math.PI * 52 * (1 - score / 100)}
                  />
                  <defs>
                    <linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
                      <stop offset="0%" stopColor="#6366f1" />
                      <stop offset="100%" stopColor="#c084fc" />
                    </linearGradient>
                  </defs>
                </svg>
                <div className="absolute inset-0 grid place-items-center -rotate-0">
                  <span className="text-3xl font-bold">{score}</span>
                </div>
              </div>
              <div className="flex-1">
                <span className="inline-block px-3 py-1 rounded-full bg-violet-500/15 text-violet-300 text-sm font-medium">{t('score_badge')}</span>
                <div className="mt-4 flex flex-col gap-1.5">
                  {scale.map((s, i) => (
                    <span key={s} className={`text-xs ${i === 2 ? "text-white font-semibold" : "text-white/40"}`}>{s}</span>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>

          <div className="lg:col-span-3 grid sm:grid-cols-2 gap-5">
            {ANATOMY.map((a, i) => (
              <motion.div
                key={a.titleKey}
                data-testid={`anatomy-card-${i}`}
                initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.45, delay: i * 0.08 }}
                className="glass rounded-2xl overflow-hidden flex"
              >
                <img src={img(a.imgKey)} alt={t(a.titleKey)} className="h-full w-24 object-cover shrink-0" />
                <div className="p-5">
                  <h4 className="font-semibold">{t(a.titleKey)}</h4>
                  <p className="text-white/55 text-xs mt-1 leading-relaxed">{t(a.descKey)}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="mt-12 text-center">
          <Button
            data-testid="iris-analysis-button"
            onClick={() => scrollTo("contact")}
            variant="outline"
            className="rounded-full h-12 px-8 bg-white/5 border-white/15 text-white hover:bg-white/10 gap-2 text-base"
          >
            {t('uniqueness_cta')} <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </section>
  );
};
